import type { Plugin } from 'vue';
import PrimeVue from 'primevue/config';
import PVAutoComplete from 'primevue/autocomplete';
import PVBadge from 'primevue/badge';
import PVButton from 'primevue/button';
import PVButtonGroup from 'primevue/buttongroup';
import PVCheckbox from 'primevue/checkbox';
import PVDrawer from 'primevue/drawer';
import PVInputText from 'primevue/inputtext';
import PVMenu from 'primevue/menu';
import PVMultiSelect from 'primevue/multiselect';
import PVPopover from 'primevue/popover';
import PVProgressSpinner from 'primevue/progressspinner';
import PVSelect from 'primevue/select';
import PVSplitButton from 'primevue/splitbutton';
import PVToggleSwitch from 'primevue/toggleswitch';

import { useAppTheme } from '@/composables/useAppTheme';
import { getActiveAppTheme } from '@/themes';
import type { Preset } from '@primeuix/themes/types';

export async function getPrimeVuePlugin(): Promise<Plugin> {
  const { setAppTheme, appTheme } = useAppTheme();
  setAppTheme(await getActiveAppTheme());

  return {
    install(vue) {
      const config: {
        licenseKey?: string;
        theme: {
          preset: Preset;
          options: {
            darkModeSelector: boolean;
          };
        };
      } = {
        theme: {
          preset: appTheme.value.primevuePreset,
          options: {
            // Client themes are light-only today; avoid system-driven dark CSS vars.
            darkModeSelector: false,
          },
        },
      };

      if (import.meta.env.VITE_PRIME_UI_LICENSE_KEY) {
        config.licenseKey = import.meta.env.VITE_PRIME_UI_LICENSE_KEY;
      }

      vue.use(PrimeVue, config);

      const components = [
        PVAutoComplete,
        PVBadge,
        PVButton,
        PVButtonGroup,
        PVCheckbox,
        PVDrawer,
        PVInputText,
        PVMenu,
        PVMultiSelect,
        PVPopover,
        PVProgressSpinner,
        PVSelect,
        PVSplitButton,
        PVToggleSwitch,
      ];

      for (const component of components) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (component as any).compatConfig = { MODE: 3 };
        vue.component(`PV${component.name}`, component);
      }
    },
  };
}
